import React, { useRef, useState } from "react";
import { ScanLine } from "lucide-react";

// Shared upload surface for every Experience Mode flow (EOB → ERA,
// Single document → one claim, …). Kept intentionally simple.
export default function FileDropzone({
  onFile,
  onFiles,
  multiple = false,
  maxFiles = 10,
  accept = ".pdf,image/png,image/jpeg",
  formats = [],
  hint,
  disabled = false,
  testId = "file-dropzone",
  inputTestId = "file-dropzone-input",
  browseTestId = "file-dropzone-browse",
}) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const open = () => { if (!disabled) inputRef.current?.click(); };
  const emit = (fileList) => {
    if (disabled) return;
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    if (multiple) onFiles?.(files.slice(0, maxFiles));
    else onFile?.(files[0]);
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="Upload a file — drag and drop or browse"
      data-testid={testId}
      onClick={open}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !disabled) { e.preventDefault(); open(); } }}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
      onDrop={(e) => { e.preventDefault(); setDrag(false); emit(e.dataTransfer.files); }}
      className={`w-full min-h-[248px] rounded-[16px] px-6 py-8 text-center cursor-pointer glass-dark text-white flex flex-col items-center justify-center select-none outline-none border border-dashed transition-colors
        ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-white/50 focus-visible:border-white"}
        ${drag ? "border-white" : "border-white/25"}`}
    >
      <div className="w-12 h-12 rounded-[12px] bg-white/[.1] border border-white/15 flex items-center justify-center mb-3">
        <ScanLine size={22} strokeWidth={1.9} />
      </div>

      <div className="text-[14px] font-semibold">{multiple ? "Drag & drop your files here" : "Drag & drop your file here"}</div>

      <button
        type="button"
        tabIndex={-1}
        data-testid={browseTestId}
        onClick={(e) => { e.stopPropagation(); open(); }}
        className="mt-3 text-[12px] font-bold px-4 py-2 rounded-full bg-white text-[#1d1d1f] hover:opacity-90 transition-opacity"
      >
        Browse files
      </button>

      {hint ? <div className="text-[11px] text-white/55 mt-3 max-w-[380px] leading-snug">{hint}</div> : null}

      {formats.length ? (
        <div className="flex gap-1.5 justify-center flex-wrap mt-4">
          {formats.map((f, i) => {
            const Ic = f.icon;
            return (
              <span key={i} className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full bg-white/[.08] border border-white/15 text-white/80">
                {Ic ? <Ic size={12} strokeWidth={2} /> : null} {f.label}
              </span>
            );
          })}
        </div>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        data-testid={inputTestId}
        onChange={(e) => { emit(e.target.files); e.target.value = ""; }}
      />
    </div>
  );
}
